import { useEffect, useRef, useState, type MouseEvent } from "react";
import { flushSync } from "react-dom";
import { useAuth } from "@clerk/react";
import { fetchGalleryData, type GalleryData } from "./api";
import type { Gallery } from "./model";
import { interceptLinkClick, navigate as navigateTo } from "./navigation";
import "./galleryTransition.css";

// Enhance ordinary links only when we can connect two rendered photographs.
// Keyboard, modified clicks, reduced motion and older browsers keep native navigation.
export function useGalleryNavigation() {
  const { getToken, userId } = useAuth();
  const [pathname, setPathname] = useState(window.location.pathname);
  const [initialData, setInitialData] = useState<GalleryData>();
  const [openingSlug, setOpeningSlug] = useState<string>();
  const pending = useRef<AbortController | null>(null);

  useEffect(() => {
    function onPopState() {
      pending.current?.abort();
      setOpeningSlug(undefined);
      setInitialData(undefined);
      setPathname(window.location.pathname);
    }
    window.addEventListener("popstate", onPopState);
    // Runs after React's handlers, so a gallery transition that already took the click wins.
    document.addEventListener("click", interceptLinkClick);
    return () => {
      window.removeEventListener("popstate", onPopState);
      document.removeEventListener("click", interceptLinkClick);
    };
  }, []);

  useEffect(() => {
    return () => { pending.current?.abort(); };
  }, [userId]);

  function openGallery(event: MouseEvent<HTMLAnchorElement>, gallery: Gallery) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.detail === 0) return;
    if (!document.startViewTransition || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const source = event.currentTarget.querySelector<HTMLImageElement>(".library-cover-image");
    if (!source?.complete || !source.naturalWidth) return;

    event.preventDefault();
    if (openingSlug === gallery.slug) return;
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    setOpeningSlug(gallery.slug);
    const href = event.currentTarget.href;
    void navigate();

    async function navigate() {
      const destination: { image: HTMLImageElement | null } = { image: null };
      try {
        const token = await getToken();
        if (controller.signal.aborted) return;
        const data = await fetchGalleryData(gallery.slug, token, controller.signal);
        if (controller.signal.aborted || !source?.isConnected) return;
        const coverPath = new URL(source.src).pathname;
        const cover = data.photos.find((photo) => !photo.isHidden && new URL(photo.mediaUrl, window.location.origin).pathname === coverPath);

        source.style.viewTransitionName = "gallery-cover";
        document.documentElement.dataset.galleryTransition = "";
        const transition = document.startViewTransition(async () => {
          if (controller.signal.aborted) return;
          window.history.pushState(null, "", href);
          flushSync(() => {
            setInitialData({ ...data, userId: userId ?? null });
            setPathname(new URL(href).pathname);
            setOpeningSlug(undefined);
          });
          window.scrollTo({ top: 0, behavior: "instant" });
          if (cover) {
            const target = document.querySelector<HTMLImageElement>(`[data-photo-id="${CSS.escape(cover.id)}"] img`);
            destination.image = target;
            if (target && target.getBoundingClientRect().top < window.innerHeight) {
              target.style.viewTransitionName = "gallery-cover";
              target.loading = "eager";
              // Never hold navigation for an original that is slow to download.
              await Promise.race([target.decode().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 120))]);
            }
          }
          document.querySelector<HTMLElement>(".gallery-hero h1")?.focus({ preventScroll: true });
        });
        const skip = () => transition.skipTransition();
        controller.signal.addEventListener("abort", skip, { once: true });
        // A skipped animation must not turn a successful navigation into an error.
        void transition.ready.catch(() => {});
        await transition.finished;
        controller.signal.removeEventListener("abort", skip);
      } catch {
        if (!controller.signal.aborted) navigateTo(new URL(href).pathname);
      } finally {
        source?.style.removeProperty("view-transition-name");
        destination.image?.style.removeProperty("view-transition-name");
        if (pending.current === controller) {
          delete document.documentElement.dataset.galleryTransition;
          pending.current = null;
          setOpeningSlug(undefined);
        }
      }
    }
  }

  return { pathname, initialData, openingSlug, openGallery };
}
